import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_CRM_API ?? '/api/v1';

export type Customer = {
  id: string;
  customer_code: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  lifetime_value: number;
  health_score: number;
  rfm_segment: string;
};

export type CustomerDashboard = {
  total_customers: number;
  active_customers: number;
  new_this_month: number;
  open_cases: number;
  total_loyalty_points: number;
  avg_health_score: number;
  high_churn_risk: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const crmApi = {
  dashboard: () => request<CustomerDashboard>('/customers/dashboard'),

  listCustomers: () => request<Customer[]>('/customers'),

  createCustomer: (body: { account_type: string; email: string; first_name: string; last_name?: string; marketplace?: string; external_id?: string }) =>
    request<Customer>('/customers', { method: 'POST', body: JSON.stringify(body) }),

  getCustomer360: (id: string) => request<unknown>(`/customers/${id}/360`),

  awardPoints: (customerId: string, points: number) =>
    request<unknown>('/loyalty/points', { method: 'POST', body: JSON.stringify({ customer_id: customerId, points }) }),

  walletCredit: (customerId: string, amount: number) =>
    request<unknown>('/wallet/credit', { method: 'POST', body: JSON.stringify({ customer_id: customerId, amount }) }),

  listSegments: () => request<unknown[]>('/customer-segments'),

  createCase: (customerId: string, subject: string) =>
    request<unknown>('/support/cases', { method: 'POST', body: JSON.stringify({ customer_id: customerId, subject }) }),

  analytics: () => request<{ total_customers: number; avg_ltv: number; vip_customers: number }>('/customers/analytics'),
};
